/* ==========================================================================
   Customizer live preview engine
   --------------------------------------------------------------------------
   Renders a to-scale visualization of the current selection (image +
   whitespace + mat + frame) into #kf-preview-stage. Driven entirely by the
   existing selection state in custom-product.js (selectedProductsProxy) and the
   already-rendered carousel DOM (frame thumbnails). No backend calls.

   custom-product.js calls window.renderLivePreview() on every option change
   (from updateProductSummary / updateImageDisplaying) and on resize. This file
   only reads state and writes CSS custom properties + state classes, so it can
   never break pricing, add-to-cart, or the summary.
   ========================================================================== */
(function () {
  "use strict";

  // Approximate frame FACE width (inches) by profile label. The labels come
  // from selectedProductsProxy.Framing.frameType (e.g. "3/4`` Face Black").
  // We read the leading fraction when present; otherwise fall back by keyword.
  // This drives how thick the frame border looks — refine as needed.
  function frameFaceInches(frameType, frameDepth) {
    var t = String(frameType || "");
    // Pull a leading fraction/number like 1/2, 3/4, 1, 1 1/2 from the label.
    var m = t.match(/(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)\s*(?:``|"|in|inch)?/i);
    if (m) {
      var raw = m[1].trim();
      var val = 0;
      if (/\s/.test(raw)) {
        // "1 1/2"
        var parts = raw.split(/\s+/);
        val = parseFloat(parts[0]) + fractionToFloat(parts[1]);
      } else if (raw.indexOf("/") !== -1) {
        val = fractionToFloat(raw);
      } else {
        val = parseFloat(raw);
      }
      if (val > 0 && val < 6) return val;
    }
    // Keyword fallbacks.
    if (/float/i.test(t)) return 0.75;
    // Last resort: derive something sane from depth, else 1".
    var d = parseFloat(String(frameDepth || "").replace(/[^\d.]/g, ""));
    return d && d < 6 ? Math.max(0.5, d * 0.5) : 1;
  }

  function fractionToFloat(str) {
    var p = String(str).split("/");
    if (p.length === 2) {
      var n = parseFloat(p[0]),
        d = parseFloat(p[1]);
      if (d) return n / d;
    }
    return parseFloat(str) || 0;
  }

  // "W/H" -> [w, h]
  function parseWH(s) {
    if (!s) return [0, 0];
    var p = String(s).split("/").map(parseFloat);
    return [p[0] || 0, p[1] || 0];
  }

  // "[color] L-x R-x T-x B-x" -> { l, r, t, b }
  function parseSides(s) {
    var out = { l: 0, r: 0, t: 0, b: 0 };
    if (!s) return out;
    var grab = function (k) {
      var m = String(s).match(new RegExp(k + "-(\\d+(?:\\.\\d+)?)", "i"));
      return m ? parseFloat(m[1]) : 0;
    };
    out.l = grab("L");
    out.r = grab("R");
    out.t = grab("T");
    out.b = grab("B");
    return out;
  }

  function matColorFrom(mattingStr) {
    if (/black/i.test(mattingStr || "")) return "#141414";
    return "#f7f7f4"; // white / default museum mat
  }

  // Precise moulding colour + material tone per frame profile. Colours were
  // matched to the actual product thumbnails (auto-sampling the angled photos
  // gave muddy averages, so these are curated to the true frame finish). Tone
  // drives the bevel: wood/painted = soft matte bevel, metal = brighter specular.
  function frameVisual(frameType) {
    var t = String(frameType || "").toLowerCase();
    // [colour, tone]  tone: 'paint' | 'wood' | 'metal'
    var color = "#2b2b2b",
      tone = "paint";

    if (/white/.test(t)) {
      color = "#ecebe7";
      tone = "paint";
    } else if (/maple/.test(t)) {
      color = "#caa468";
      tone = "wood";
    } else if (/walnut/.test(t)) {
      color = "#6b4630";
      tone = "wood";
    } else if (/silver/.test(t)) {
      color = "#b6b8bb";
      tone = "metal";
    } else if (/gold/.test(t)) {
      color = "#c8a24e";
      tone = "metal";
    } else if (/bronze/.test(t)) {
      color = "#7a5a3a";
      tone = "metal";
    } else if (/brown/.test(t)) {
      color = "#6e4a33";
      tone = "wood";
    } else if (/black/.test(t)) {
      color = "#2b2b2b";
      tone = "paint";
    } else {
      color = "#2b2b2b";
      tone = "paint";
    }
    return { color: color, tone: tone };
  }

  function cssEscape(s) {
    return String(s).replace(/"/g, '\\"');
  }

  function num(n) {
    return isFinite(n) && n > 0 ? n : 0;
  }

  // ── main render ──────────────────────────────────────────────────────────
  function renderLivePreview() {
    var stage = document.getElementById("kf-preview-stage");
    var container = document.getElementById("customImageContainer");
    if (!stage || !container) return;

    // No engine state yet? (e.g. called too early) bail gracefully.
    var proxy =
      typeof selectedProductsProxy !== "undefined" ? selectedProductsProxy : null;

    var imageData = document.getElementById("imageData");
    // The customizer sets imageData.value to a FRAME thumbnail when a frame is
    // picked with no uploaded art, so imageData.value alone is unreliable. The
    // theme's own `isClientImageUploaded` flag is the source of truth for
    // "the customer actually uploaded/selected artwork".
    var clientUploaded =
      typeof isClientImageUploaded !== "undefined"
        ? !!isClientImageUploaded
        : !!(imageData && imageData.value);
    // A genuine artwork data-URL / GCS image (not a Shopify CDN frame thumbnail).
    var dataVal = (imageData && imageData.value) || "";
    var looksLikeArt =
      /^data:image/.test(dataVal) ||
      /storage\.googleapis\.com/.test(dataVal);
    var hasArtwork = clientUploaded || looksLikeArt;

    var printing = (proxy && proxy["Printing"]) || {};
    var matting = (proxy && proxy["Matting"]) || {};
    var framing = (proxy && proxy["Framing"]) || {};

    // FRAME-ONLY: a frame is chosen but no artwork uploaded (someone buying just
    // the frame). Render the frame around a blank interior instead of the empty
    // mockup, so they still see the frame they're configuring.
    var frameOnlyNoArt = !hasArtwork && proxy && !!framing.frameType;

    // EMPTY state: nothing uploaded AND no frame chosen → show the default
    // placeholder mockup as-is.
    if ((!hasArtwork && !frameOnlyNoArt) || !proxy) {
      stage.classList.add("kf-preview--empty");
      stage.classList.remove(
        "has-glazing", "has-mat", "print-only",
        "is-canvas", "is-mounted", "is-stretched", "is-float"
      );
      stage.style.width = "82%";
      stage.style.height = "82%";
      return;
    }
    stage.classList.remove("kf-preview--empty");
    stage.classList.toggle("frame-only-blank", frameOnlyNoArt);

    // ---- gather inches ----
    var img = parseWH(printing.imageSize);
    var imgW = num(img[0]);
    var imgH = num(img[1]);
    if (frameOnlyNoArt) {
      // No artwork — use the chosen frame size if set, else a default portrait,
      // and DON'T derive aspect from the frame-thumbnail image.
      var fs = parseWH(framing.frameSize);
      imgW = num(fs[0]) || 12;
      imgH = num(fs[1]) || 16;
    } else if (!imgW || !imgH) {
      // fall back to the natural aspect of the loaded image
      var artEl = document.getElementById("customImage");
      if (artEl && artEl.naturalWidth && artEl.naturalHeight) {
        imgW = artEl.naturalWidth;
        imgH = artEl.naturalHeight;
      } else {
        imgW = 10;
        imgH = 10;
      }
    }

    // Whitespace and matboard are DISTINCT layers, not one combined band:
    //   • whitespace  = white border printed around the artwork (inner layer)
    //   • matboard    = black/white mat around the whitespace (outer layer)
    // Frame-only has no print, so neither.
    var ws = frameOnlyNoArt ? { l: 0, r: 0, t: 0, b: 0 } : parseSides(printing.whitespace);
    var mat = frameOnlyNoArt ? { l: 0, r: 0, t: 0, b: 0 } : parseSides(matting.matting);
    var hasWs = ws.l + ws.r + ws.t + ws.b > 0;
    var hasMat = mat.l + mat.r + mat.t + mat.b > 0;

    // Total band per side (for scene sizing) = matboard (outer) + whitespace (inner).
    var bandL = ws.l + mat.l;
    var bandR = ws.r + mat.r;
    var bandT = ws.t + mat.t;
    var bandB = ws.b + mat.b;

    var hasFrame = !!framing.frameType;
    var faceIn = hasFrame ? frameFaceInches(framing.frameType, framing.frameDepth) : 0;

    var hasGlazing = !!framing.glazing;

    // ---- material / printing-type style ----
    var style = materialStyle(printing.printingMaterial, printing.printingType);
    // Float frame: the artwork floats inside the frame with a visible gap
    // (a shadow reveal) instead of sitting flush behind a mat.
    var isFloat = /float/i.test(framing.floating || "");
    // Float reveal gap (inches) — a small constant reveal between art and frame.
    var floatGapIn = isFloat && hasFrame ? 0.35 : 0;
    // Canvas wrapped-edge depth (inches) drives the 3D side thickness.
    var depthIn = style.isCanvas ? canvasDepthInches(printing.stretchedDepth) : 0;

    // ---- total scene size in inches ----
    var totalW = imgW + bandL + bandR + (faceIn + floatGapIn) * 2;
    var totalH = imgH + bandT + bandB + (faceIn + floatGapIn) * 2;

    // ---- fit to container ----
    var cw = container.clientWidth || 1;
    var ch = container.clientHeight || 1;
    var pad = 0.9; // leave a margin so the wall shadow has room to breathe
    var pxPerIn = Math.min((cw * pad) / totalW, (ch * pad) / totalH);
    if (!isFinite(pxPerIn) || pxPerIn <= 0) pxPerIn = 4;

    var sceneWpx = totalW * pxPerIn;
    var sceneHpx = totalH * pxPerIn;

    // ---- write CSS vars ----
    var fv = frameVisual(framing.frameType);
    var s = stage.style;
    s.setProperty("--kf-scene-w", sceneWpx + "px");
    s.setProperty("--kf-scene-h", sceneHpx + "px");
    s.setProperty("--kf-frame-px", faceIn * pxPerIn + "px");
    // Matboard band (outer, .kf-preview-mat padding) = matting inches only.
    s.setProperty("--kf-mat-l-px", mat.l * pxPerIn + "px");
    s.setProperty("--kf-mat-r-px", mat.r * pxPerIn + "px");
    s.setProperty("--kf-mat-t-px", mat.t * pxPerIn + "px");
    s.setProperty("--kf-mat-b-px", mat.b * pxPerIn + "px");
    // Whitespace band (inner, .kf-preview-whitespace padding) = whitespace inches.
    s.setProperty("--kf-ws-l-px", ws.l * pxPerIn + "px");
    s.setProperty("--kf-ws-r-px", ws.r * pxPerIn + "px");
    s.setProperty("--kf-ws-t-px", ws.t * pxPerIn + "px");
    s.setProperty("--kf-ws-b-px", ws.b * pxPerIn + "px");
    // Frame: solid moulding colour + a miter-correct bevel built from per-SIDE
    // border colours (top/left lighter, bottom/right darker) so corners are
    // never mis-tinted. Metal tones get a stronger highlight contrast.
    var hiAmt = fv.tone === "metal" ? 0.55 : fv.tone === "wood" ? 0.3 : 0.34;
    var loAmt = fv.tone === "metal" ? -0.42 : -0.4;
    s.setProperty("--kf-frame-color", fv.color);
    s.setProperty("--kf-frame-edge", shade(fv.color, loAmt));
    s.setProperty("--kf-frame-hi", shade(fv.color, hiAmt));
    stage.setAttribute("data-frame-tone", fv.tone);
    s.setProperty("--kf-mat-color", matColorFrom(matting.matting));
    s.setProperty("--kf-float-gap-px", floatGapIn * pxPerIn + "px");
    s.setProperty("--kf-canvas-depth-px", Math.max(0, depthIn * pxPerIn) + "px");
    s.setProperty("--kf-canvas-edge", style.canvasEdgeColor(printing.canvasEdges));
    s.width = sceneWpx + "px";
    s.height = sceneHpx + "px";

    // ---- state classes ----
    // Material / printing-type as data attributes so the CSS can give each its
    // own surface (canvas weave, photo gloss, mounted acrylic sheen, …).
    stage.setAttribute("data-material", style.material);
    stage.setAttribute("data-finish", style.finish);   // matte | satin | gloss | metallic | acrylic | dibond | flat
    stage.classList.toggle("has-glazing", hasFrame && hasGlazing && style.allowsGlazing);
    stage.classList.toggle("has-mat", hasMat && style.allowsMat);
    stage.classList.toggle("has-ws", hasWs && style.allowsMat);
    stage.classList.toggle("print-only", !hasFrame);
    stage.classList.toggle("is-canvas", style.isCanvas);
    stage.classList.toggle("is-mounted", style.isMounted);
    stage.classList.toggle("is-stretched", style.isCanvas && depthIn > 0);
    stage.classList.toggle("is-float", isFloat && hasFrame);
  }

  // Classify the selected material + printing type into a render style.
  function materialStyle(material, printingType) {
    var m = String(material || "").toLowerCase();
    var p = String(printingType || "").toLowerCase();

    var isCanvas = /canvas/.test(m);
    var isFineArt = /fine\s*art/.test(m);
    var isPhotograde = /photograde/.test(m);
    var isMounted = /mounted/.test(m);

    // finish drives the surface sheen
    var finish = "matte";
    if (isMounted) {
      if (/dibond|aluminum|aluminium/.test(p)) finish = "dibond";
      else if (/metallic/.test(p)) finish = "metallic";
      else finish = "acrylic"; // glossy acrylic face-mount
    } else if (isPhotograde) {
      if (/metallic/.test(p)) finish = "metallic";
      else if (/gloss/.test(p)) finish = "gloss";
      else if (/lustre|satin/.test(p)) finish = "satin";
      else finish = "satin";
    } else if (isCanvas) {
      finish = /satin/.test(p) ? "satin" : "matte";
    } else {
      // fine art
      finish = "matte";
    }

    var materialKey = isCanvas
      ? "canvas"
      : isFineArt
      ? "fineart"
      : isPhotograde
      ? "photograde"
      : isMounted
      ? "mounted"
      : "generic";

    return {
      material: materialKey,
      finish: finish,
      isCanvas: isCanvas,
      isFineArt: isFineArt,
      isPhotograde: isPhotograde,
      isMounted: isMounted,
      // Canvas and mounted prints are NOT matted or glazed in production.
      allowsMat: !isCanvas && !isMounted,
      allowsGlazing: !isCanvas && !isMounted,
      canvasEdgeColor: function (edges) {
        var e = String(edges || "").toLowerCase();
        if (/white/.test(e)) return "#f3f1ec";
        if (/black/.test(e)) return "#171717";
        // "Image mirrored" or default → reuse the artwork colour; approximate
        // with a neutral mid that reads as a wrapped print edge.
        return "#8a8278";
      },
    };
  }

  // Canvas stretched depth → wrapped edge thickness in inches.
  function canvasDepthInches(stretchedDepth) {
    var d = String(stretchedDepth || "").toLowerCase();
    if (/1\s*1\/2|gallery/.test(d)) return 1.5;
    if (/3\/4|regular/.test(d)) return 0.75;
    var n = parseFloat(d.replace(/[^\d.]/g, ""));
    return isFinite(n) && n > 0 ? n : 0.75; // default to a regular wrap
  }

  // Lighten (amt>0) or darken (amt<0) a hex colour by a fraction. Used to make
  // the frame bevel highlight/shadow from the base moulding colour.
  function shade(hex, amt) {
    var m = String(hex).replace("#", "");
    if (m.length === 3) m = m[0] + m[0] + m[1] + m[1] + m[2] + m[2];
    var r = parseInt(m.slice(0, 2), 16),
      g = parseInt(m.slice(2, 4), 16),
      b = parseInt(m.slice(4, 6), 16);
    var f = amt < 0 ? 0 : 255,
      p = Math.abs(amt);
    r = Math.round((f - r) * p + r);
    g = Math.round((f - g) * p + g);
    b = Math.round((f - b) * p + b);
    return "rgb(" + r + "," + g + "," + b + ")";
  }

  // Expose globally so custom-product.js can call it. We render immediately AND
  // schedule one more pass on the next frame. Reason: when an option is first
  // selected, the customizer mutates several proxy values + expands accordions
  // in the same tick, so the first synchronous render can compute a slightly
  // wrong fit (observed: a frame's first paint came out ~9% too tall and the
  // bottom looked clipped until the next option change re-rendered it). The
  // deferred pass re-fits after that settles, so the correct size always wins.
  var raf =
    window.requestAnimationFrame ||
    function (cb) {
      return setTimeout(cb, 16);
    };
  var deferred = null;
  window.renderLivePreview = function () {
    renderLivePreview();
    if (deferred) return; // coalesce
    deferred = raf(function () {
      raf(function () {
        deferred = null;
        renderLivePreview();
      });
    });
  };

  // Re-fit on resize (custom-product.js also calls it from its resize handler;
  // this is a harmless extra safety net, debounced).
  var rt = null;
  window.addEventListener("resize", function () {
    if (rt) clearTimeout(rt);
    rt = setTimeout(renderLivePreview, 120);
  });

  // Initial paint once the DOM (and the default image) is ready.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      setTimeout(renderLivePreview, 0);
    });
  } else {
    setTimeout(renderLivePreview, 0);
  }
})();
